import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SchemaOrgData {
  '@context': string;
  '@type': string;
  [key: string]: unknown;
}

export function useSchemaOrg(data: SchemaOrgData | SchemaOrgData[]) {
  const location = useLocation();

  useEffect(() => {
    const schemas = Array.isArray(data) ? data : [data];
    const scriptElements: HTMLScriptElement[] = [];

    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `schema-org-${location.pathname}-${index}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      scriptElements.push(script);
    });

    return () => {
      scriptElements.forEach((script) => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, [data, location.pathname]);
}

export function generateLocalBusinessSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${siteUrl}/#organization`,
    name: 'Best Call Restoration',
    image: `${siteUrl}/logo.png`,
    url: siteUrl,
    telephone: '+1-321-947-6724',
    email: 'bestcallrestor@gmail.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '801 Copenhagen Way',
      addressLocality: 'Winter Garden',
      addressRegion: 'FL',
      postalCode: '34787',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 28.5651,
      longitude: -81.5921
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '08:00',
        closes: '18:00'
      }
    ],
    priceRange: '$$',
    areaServed: {
      '@type': 'City',
      name: 'Winter Garden',
      '@id': 'https://en.wikipedia.org/wiki/Winter_Garden,_Florida'
    },
    serviceArea: {
      '@type': 'GeoCircle',
      geoMidpoint: {
        '@type': 'GeoCoordinates',
        latitude: 28.5651,
        longitude: -81.5921
      },
      geoRadius: '50000'
    }
  };
}

export function generateWebSiteSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    url: siteUrl,
    name: 'Best Call Restoration',
    description: 'Professional home remodeling services in Winter Garden, FL',
    publisher: {
      '@id': `${siteUrl}/#organization`
    }
  };
}

export function generateServiceSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const services = [
    {
      name: 'Bathroom Remodeling',
      description: 'Complete bathroom renovation services including design, installation, and finishing'
    },
    {
      name: 'Kitchen Remodeling',
      description: 'Professional kitchen renovation with custom cabinets, countertops, and modern appliances'
    },
    {
      name: 'Interior Painting',
      description: 'Expert interior painting services for residential properties'
    },
    {
      name: 'Exterior Painting',
      description: 'Professional exterior painting and surface preparation'
    },
    {
      name: 'Flooring Installation',
      description: 'Quality flooring installation including hardwood, tile, and laminate'
    },
    {
      name: 'Drywall Work',
      description: 'Professional drywall installation, repair, and finishing services'
    }
  ];

  return services.map((service) => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: service.name,
    description: service.description,
    provider: {
      '@id': `${siteUrl}/#organization`
    },
    areaServed: {
      '@type': 'City',
      name: 'Winter Garden',
      '@id': 'https://en.wikipedia.org/wiki/Winter_Garden,_Florida'
    }
  }));
}

export function generateBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`
    }))
  };
}

export function generateFAQSchema(faqs: Array<{ question: string; answer: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
}
